import React from 'react';
import { Box, Divider, List, ListItem, Typography } from '@mui/material';
import { ListItemBlockProps } from './types';
import { Link } from 'react-router-dom';
import StarIcon from '../../../assets/svg/kid_star.svg?react';
import AddIcon from '../../../assets/svg/add-icon.svg?react';
import MenuBlock from '../menu-block';
import { ProgressOverlay } from '../progress-overlay/ProgressOverlay';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { useAppSelector } from '../hooks/useAppSelector';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';

const ListItemBlock = ({
  id,
  headerText,
  withLinkButton,
  listArray,
  linkURL,
  emptyStateMessage,
  withMenu,
  menuOptions,
  loading,
  withFixedMaximumHeight,
  orderedList,
}: ListItemBlockProps) => {
  const { t } = useTranslation();
  const company = useAppSelector(selectAllCompanies)[0];
  return (
    <div className='list-item-block' style={{ backgroundColor: '#fff', borderRadius: '8px' }}>
      {(headerText || withLinkButton) && (
        <Box
          className='list-header'
          sx={{
            justifyContent: headerText ? 'space-between' : 'flex-end',
          }}
        >
          {headerText && (
            <Typography variant='h2' className='header-text'>
              {headerText}
            </Typography>
          )}
          {withLinkButton && (
            <Box
              id={`${id ?? 'listItem'}AddLink`}
              component={Link}
              to={linkURL ?? ''}
              className='add-icon-wrapper'
              sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            >
              <AddIcon id={`${id ?? 'listItem'}AddLinkAddIcon`} />
            </Box>
          )}
          {withMenu && <MenuBlock id={`${id ?? 'listItem'}ItemMenu`} options={menuOptions ?? []} />}
        </Box>
      )}
      {loading ? (
        <ProgressOverlay
          position='relative'
          className={withFixedMaximumHeight ? 'fixed-max-height' : undefined}
          sx={{ flexGrow: 1 }}
        />
      ) : (
        <>
          {listArray.length > 0 ? (
            <List
              className={withFixedMaximumHeight ? 'list-content fixed-max-height' : 'list-content'}
              sx={{
                padding: headerText || withLinkButton ? '0 16px 16px' : '16px',
              }}
            >
              {listArray.map((item, i) => (
                <React.Fragment key={`${item.id}-${uuidv4()}`}>
                  <ListItem
                    id={`${id ?? 'listItem'}ListItemArea_${i}`}
                    className='list-item'
                    onClick={item.onListItemClick}
                    sx={{
                      cursor: item.onListItemClick ? 'pointer' : 'initial',
                      '&:hover': {
                        bgcolor: '#F8F9FC',
                      },
                    }}
                  >
                    {item.isFavourite && (
                      <StarIcon id={`${id ?? 'listItem'}ListItemAreaStarIcon_${i}`} />
                    )}
                    <Typography
                      id={`${id ?? 'listItem'}ListItemAreaText_${i}`}
                      className='item-name'
                    >
                      {orderedList ? i + 1 + '. ' : ''}
                      {item.name}
                    </Typography>
                    {item.isFreeLabel &&
                      !company?.stripe_subscription &&
                      !company?.stripe_product_id && (
                        <Box
                          id={`${id ?? 'listItem'}ListItemAreaItemDetails_${i}`}
                          className='item-details'
                        >
                          <Typography
                            id={`${id ?? 'listItem'}ListItemAreaText2_${i}`}
                            className='free-label'
                          >
                            {t('labels.free')}
                          </Typography>
                        </Box>
                      )}
                    {item.labelText && !item.withDate && (
                      <Box
                        id={`${id ?? 'listItem'}ListItemAreaItemDetails2_${i}`}
                        className='item-details'
                      >
                        <Typography
                          id={`${id ?? 'listItem'}ListItemAreaText3_${i}`}
                          className='label-text'
                        >
                          {item.labelText}
                        </Typography>
                      </Box>
                    )}
                    {item.labelText && item.withDate && (
                      <Box
                        id={`${id ?? 'listItem'}ListItemAreaItemDetails3_${i}`}
                        className='item-details with-date'
                      >
                        <CalendarMonthOutlinedIcon
                          id={`${id ?? 'listItem'}ListItemAreaCalendarMonthIcon_${i}`}
                          fontSize='small'
                        />
                        <Typography
                          id={`${id ?? 'listItem'}ListItemAreaText4_${i}`}
                          className='label-text with-date'
                        >
                          {item.labelText}
                        </Typography>
                      </Box>
                    )}
                  </ListItem>
                  <Divider className='item-divider' />
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box
              className={
                'empty-state-message-container' +
                (withFixedMaximumHeight ? ' fixed-max-height' : '')
              }
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <div className='empty-state-message'>{emptyStateMessage}</div>
            </Box>
          )}
        </>
      )}
    </div>
  );
};

export default ListItemBlock;
